/**
 * Shared auth-failure detection for voucher reconcile/convert submit paths.
 *
 * A session that has expired mid-form (e.g. the staff member left the
 * Program Voucher dialog open past their auth window) does not fail like a
 * normal domain error: the API layer typically responds with a 401, a
 * redirect to a login page, or an HTML error page instead of the expected
 * JSON body. Surfacing that as a generic "Reconciliation failed" message
 * hides the real cause from staff, so it is detected and reported
 * separately here.
 */

export const SESSION_EXPIRED_MESSAGE = 'Your session expired. Refresh the page and try again.';

/**
 * True when a fetch Response looks like an auth failure rather than a
 * normal domain-level error: a 401/403 status, a redirected response
 * (typically bounced to a login page), or a non-ok response with an HTML
 * body (typically a login page served without a redirect).
 *
 * Any JSON body - including `application/problem+json` from ASP.NET Core
 * model-validation failures - falls through to normal domain-error
 * handling and is never classified as an auth failure, even on a non-2xx
 * status. This keeps a legit validation 400 or a JSON domain error from
 * being masked as "your session expired".
 */
export function isAuthFailureResponse(response: Response): boolean {
    if (response.status === 401 || response.status === 403) return true;
    if (response.redirected) return true;
    if (!response.ok) {
        const contentType = response.headers.get('content-type') ?? '';
        if (contentType.includes('text/html')) return true;
    }
    return false;
}

/** Console breadcrumb for an auth-shaped failure, including response status. */
export function logAuthFailure(context: string, response: Response): void {
    console.error(`[${context}] auth-shaped failure detected: status=${response.status} redirected=${response.redirected} url=${response.url}`);
}
